import { FILTER_VALUE_SEPARATOR } from '@aibees/elephant/constants/filters';
import { MetadataItem } from '@aibees/elephant/hooks/types';
import { SearchBrowseParams } from '@aibees/shared/definition';
import { useCallback, useMemo } from 'react';
import { SearchFilter, SearchFilterOption } from '../..';
import { EnrichedCheckboxOption } from './types';

export function useSelectAllItemState({
  params,
  setParams,
  field,
  options,
  metadata,
}: {
  params: Partial<SearchBrowseParams>;
  setParams: (params: Record<string, string>) => void;
  field?: string;
  options?: (SearchFilterOption & MetadataItem)[];
  metadata: SearchFilter['metadata'];
}) {
  const isAllSelected = useMemo(() => {
    if (params[field!]) {
      const selectedValues = params[field!]?.split(FILTER_VALUE_SEPARATOR);
      return options?.every((option) => selectedValues?.includes(option.value));
    } else {
      return options?.every((option) => option.selected);
    }
  }, [options, params, field]);

  const selectAllCheckboxOption = useMemo(() => {
    return {
      value: 'select-all',
      label: isAllSelected ? 'Deselect All' : 'Select All',
      checked: isAllSelected,
      indeterminate: !isAllSelected,
      disabled: false,
      comingSoon: false,
      type: 'STRING',
    } as EnrichedCheckboxOption;
  }, [isAllSelected]);

  const handleSelectAllCheckboxClick = useCallback(() => {
    setParams({
      [field!]: isAllSelected
        ? options
            ?.filter((option) =>
              metadata?.checkbox?.deselectAllExceptionKeys?.includes(
                option.value
              )
            )
            .map((option) => option.value)
            .join(FILTER_VALUE_SEPARATOR) || ''
        : options?.map((option) => option.value).join(FILTER_VALUE_SEPARATOR) ||
          '',
    });
  }, [
    field,
    setParams,
    isAllSelected,
    options,
    metadata?.checkbox?.deselectAllExceptionKeys,
  ]);

  return {
    selectAllCheckboxOption,
    handleSelectAllCheckboxClick,
  };
}
